import React, { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  ActivityItem,
  ActivityResponseDto,
  ActivityType,
} from "@/modules/lead-page/dtos/index-v2";
import { ActivityContext } from "@/modules/lead-page/new/hooks/activity-context.hook";
import { ActivityContextType } from "@/modules/lead-page/new/context/activity.context";
import { CursorInterestActionsResponse, InterestAction } from "../../types";
import { queryInterestActionsCursor } from "../../api/lead-page.v2.api";

export const InterestActionsProvider: React.FC<{
  children: React.ReactNode;
  interestUids: string[];
  enabled: boolean;
}> = ({ children, interestUids, enabled }) => {
  const {
    data,
    fetchNextPage,
    hasNextPage = false,
    isFetchingNextPage = false,
    isLoading = false,
    isError = false,
    isFetching,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["interest-actions", interestUids],
    queryFn: async ({ pageParam }) => {
      return queryInterestActionsCursor({
        interestUids: interestUids,
        take: 20,
        cursorId: pageParam,
      });
    },
    enabled,
    select: (data) => {
      return {
        pages: data.pages.map((page) =>
          mapInterestActionResponseToActivityResponse(page.data)
        ),
        pageParams: data.pageParams,
      };
    },
    getNextPageParam: (lastPage) => lastPage.data.nextCursorId ?? undefined,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const [deletingId, setDeletingId] = useState<number | string | undefined>();

  const [type, setType] = useState<ActivityType[]>([
    ActivityType.InterestUpdate,
  ]);

  const filters = useMemo(
    () => ({
      type,
      setType,
    }),
    [type]
  );

  const value: ActivityContextType = {
    filters,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
    isError,
    forceRefetch: refetch,
    handleDeleteComments: () => {},
    setDialogOpenConfirm: () => {},
    dialogOpenConfirm: false,
    deletingId,
    setDeletingId,
    highlightedActivityId: undefined,
    setHighlightedActivityId: () => {},
  };

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
};

function getActor(addedBy?: { name?: string; color?: string }) {
  return {
    actorName: addedBy?.name ?? null,
    actorColor: addedBy?.color ?? null,
  };
}

function mapInterestActionResponseToActivityResponse(
  input: CursorInterestActionsResponse
): ActivityResponseDto {
  const items: ActivityItem[] = input.data.map((update) =>
    mapInterestActionToActivityItem(update)
  );

  return {
    data: {
      items,
      count: input.count,
      nextCursor: input.nextCursorId?.toString() ?? "",
    },
  };
}

function mapInterestActionToActivityItem(action: InterestAction): ActivityItem {
  const { actorName, actorColor } = getActor(action.addedBy);

  return {
    type: ActivityType.InterestUpdate,
    id: action.id,
    uid: action.uid as any,
    currentJourneyId: 0,
    oldJourneyId: null,
    oldJourneyNanoId: null,
    formData: {},
    content: action.comment ?? null,
    actorName,
    actorColor,
    previousUser: null,
    previousUserColor: null,
    newUser: null,
    newUserColor: null,
    previousStatus: null,
    previousStatusColor: null,
    newStatus: action.interestActionType.name,
    newStatusColor: action.interestActionType.color ?? "",
    assignmentType: null,
    mentions: [],
    createdAt: action.createdAt,
    deletedAt: null,
  };
}
